import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

const fmt = (v, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(v || 0);

export default function PublicQuotePage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/quote/${token}`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch(() => setError("Quote not found or link expired."));
  }, [token]);

  const respond = async (action) => {
    if (!window.confirm(action === "accept" ? "Accept this quote?" : "Decline this quote?")) return;
    setResponding(true);
    try {
      const r = await axios.post(`${API}/public/quote/${token}/respond`, { action }, { withCredentials: false });
      setData((d) => ({ ...d, quote: { ...d.quote, status: r.data.status } }));
    } catch { setError("Could not submit your response — please try again."); }
    setResponding(false);
  };

  if (error && !data) return <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6"><p className="text-sm text-muted-foreground" data-testid="quote-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const q = data.quote;
  const decided = q.status === "accepted" || q.status === "declined";

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4" data-testid="public-quote-page">
      <div className="max-w-xl mx-auto bg-card border rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          {data.business?.logo && <img src={data.business.logo} alt="logo" className="h-10 max-w-[140px] object-contain" />}
          <div>
            <p className="font-bold text-lg leading-tight">{data.business?.business_name || "Quote"}</p>
            {data.business?.business_website && <p className="text-xs text-muted-foreground">{data.business.business_website}</p>}
          </div>
        </div>

        {q.status === "accepted" && <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-500/10 border border-emerald-500/25 rounded-lg p-3 mb-4" data-testid="quote-accepted-banner"><CheckCircle2 className="h-4 w-4" />You accepted this quote. We'll be in touch shortly!</div>}
        {q.status === "declined" && <div className="flex items-center gap-2 text-sm text-red-700 bg-red-500/10 border border-red-500/25 rounded-lg p-3 mb-4" data-testid="quote-declined-banner"><XCircle className="h-4 w-4" />You declined this quote.</div>}

        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold" data-testid="quote-number">Quote {q.number}</h1>
            <p className="text-sm text-muted-foreground">For {q.party_name}{q.party_company ? ` · ${q.party_company}` : ""}</p>
          </div>
          {q.extra_date && <p className="text-xs text-muted-foreground">Valid until {q.extra_date}</p>}
        </div>

        <div className="border rounded-xl overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr><th className="text-left p-2.5 font-medium">Description</th><th className="text-center p-2.5 font-medium">Qty</th><th className="text-right p-2.5 font-medium">Amount</th></tr></thead>
            <tbody>
              {(q.items || []).map((it, i) => (
                <tr key={i} className="border-t"><td className="p-2.5">{it.description}</td><td className="p-2.5 text-center">{it.qty}</td><td className="p-2.5 text-right">{fmt((it.qty || 0) * (it.unit_price || 0), q.currency)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-right text-sm space-y-0.5 mb-6">
          <p className="text-muted-foreground">Subtotal {fmt(q.subtotal, q.currency)} · Tax ({q.tax_rate || 0}%) {fmt(q.tax, q.currency)}</p>
          <p className="text-2xl font-bold" data-testid="quote-total">{fmt(q.total, q.currency)}</p>
        </div>
        {q.notes && <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3 mb-6">{q.notes}</p>}

        {!decided && (
          <div className="grid grid-cols-2 gap-3">
            <Button className="h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => respond("accept")} disabled={responding} data-testid="quote-accept-btn">
              {responding ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="h-5 w-5 mr-2" />Accept quote</>}
            </Button>
            <Button variant="outline" className="h-12 text-base text-red-600 border-red-300 hover:bg-red-50" onClick={() => respond("decline")} disabled={responding} data-testid="quote-decline-btn">
              <XCircle className="h-5 w-5 mr-2" />Decline
            </Button>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground text-center mt-4">Powered by Opero Smart</p>
      </div>
    </div>
  );
}
